import connect from 'components/Shared/connect';
import {
  getRedReviews,
  getTotalNumberOfReviews,
  getRatingsPerLevel,
  getMostPopularReview
} from 'views/ProductPage/selector';
import { hasElements, NO_ELEMENT, rm } from 'utils';
import { RatingsPerLevel } from './ratingsPerLevel';
import { Review as ReviewComponent } from './review';
import { isMobileLayout } from 'components/Shared/responsive';
import styles from './styles.scss';
import { ButtonProps, LoaderButton } from 'components/Button';
import cn from 'classnames';
import { IconArrow } from 'components/Shared/Icon/themes/BGH-RD3/iconArrow';
import { RedReview } from 'models/RedReview';
import {
  getRedReviews as getProductPageReviewsAction,
  upvoteProductPageReview as upvoteProductPageReviewAction
} from './actions';
import { RedReviews } from 'models/RedReviews';
import { LoaderButtonProps } from 'components/Shared/Loader/Button';
import { RatingPerLevel } from 'models/RatingPerLevel';
import { getQaDataAttr } from '@bygghemmait/utilities';

export const REVIEWS_ID = 'productReviews';

interface MergedProps {
  productReviews: RedReviews;
  ratingsPerLevel: RatingPerLevel[];
  mostPopularReview?: RedReview;
  isMobileLayout: boolean;
  numberOfReviews: number;
  channel: string;
  loadMoreReviews: () => Promise<void>;
  upvoteReview: (reviewId: string) => Promise<void>;
}

interface HelpfulButtonProps extends ButtonProps {
  amount: number;
  upvoteReview: () => Promise<void>;
}
const HelpfulButton = ({ amount, upvoteReview, ...props }: HelpfulButtonProps) => {
  return (
    <LoaderButton
      {...props}
      onClick={upvoteReview}
      className={cn(styles.helpfulButton, styles.commonButtonStyles)}
      appearance="tertiary"
      size="medium"
    >
      <span className={styles.helpfulButtonText}>
        {rm('product_review_helpful')}{' '}
        <span className={styles.helpfulButtonAmount}>({amount})</span>
      </span>
    </LoaderButton>
  );
};

interface MoreReviewsButtonProps extends LoaderButtonProps {
  loadMore: () => Promise<void>;
}
const MoreReviewsButton = ({ children, className, loadMore }: MoreReviewsButtonProps) => (
  <LoaderButton
    onClick={loadMore}
    className={cn(styles.commonButtonStyles, styles.moreReviews, className)}
    size="large"
    appearance="tertiary"
    {...getQaDataAttr('load-more-reviews-button')}
  >
    <span>{children}</span>
    <IconArrow appearance="flip-90" size="tiny" />
  </LoaderButton>
);

interface GetButtonComponent {
  review: RedReview;
  isMobileLayout: boolean;
  upvoteReview: (reviewId: string) => Promise<void>;
}
const getButtonComponent = ({ upvoteReview, review }: GetButtonComponent) => {
  const _upvoteReview = () => upvoteReview(review.reviewId);

  return (
    <HelpfulButton
      disabled={review.hasVoted}
      upvoteReview={_upvoteReview}
      amount={review.helpful}
    />
  );
};

export const _ProductReviews = ({
  productReviews: { reviews },
  isMobileLayout,
  channel,
  loadMoreReviews,
  upvoteReview,
  numberOfReviews,
  ratingsPerLevel,
  mostPopularReview
}: MergedProps) => {
  if (numberOfReviews === 0 || !hasElements(reviews)) {
    return NO_ELEMENT;
  }

  const isMoreReviews = reviews.length < numberOfReviews;
  const reviewContainerClassName = isMobileLayout
    ? styles.phoneReviewMargins
    : styles.desktopReviewContainer;
  return (
    <div className={reviewContainerClassName}>
      <div className={styles.ratings}>
        <RatingsPerLevel levels={ratingsPerLevel} isMobileLayout={isMobileLayout} />
      </div>
      {reviews.map((review, index) => (
        <ReviewComponent
          {...review}
          channel={channel}
          isMobileLayout={isMobileLayout}
          key={index}
          isMostPopular={!isMobileLayout && mostPopularReview?.reviewId === review.reviewId}
          button={getButtonComponent({
            isMobileLayout,
            review,
            upvoteReview
          })}
        />
      ))}
      {isMoreReviews && (
        <MoreReviewsButton loadMore={loadMoreReviews} className={styles.loadMoreReviewsButton}>
          {rm('product_review_show_more_reviews')}
        </MoreReviewsButton>
      )}
    </div>
  );
};

export const ProductReviews = connect<MergedProps>(
  state => ({
    channel: state.appShell.channel,
    productReviews: getRedReviews(state.currentPage),
    isMobileLayout: isMobileLayout(state),
    numberOfReviews: getTotalNumberOfReviews(state),
    ratingsPerLevel: getRatingsPerLevel(state.currentPage),
    mostPopularReview: getMostPopularReview(state.currentPage)
  }),
  dispatch => ({
    loadMoreReviews: () => dispatch(getProductPageReviewsAction()),
    upvoteReview: (reviewId: string) => dispatch(upvoteProductPageReviewAction(reviewId))
  })
)(_ProductReviews);
